import React from 'react';
import './PrintableStructureReport.css'; // Assuming a similar CSS file structure
import { format } from 'date-fns';

// Interfaces based on data structure from report.controller.js getStructureReport
interface StructureReportItem {
  id: number;
  code: string;
  type: string;
  location: string;
  isLED: boolean;
  dailyPrice: number;
  monthlyPrice: number;
  occupancyRate: number;
  occupiedDays: number;
  totalDays: number;
  revenue: number;
  orderCount: number;
  avgDuration: number;
  clientCount: number;
  popularityRank: number;
  clientDistribution: Array<{ id: number; name: string; count: number }>;
}

interface StructureReportData {
  summary: {
    totalRevenue: number;
    averageOccupancyRate: number;
    totalStructures: number;
    totalOrders: number;
  };
  mostPopularStructures: StructureReportItem[]; // Assuming top 5 are included
  mostProfitableStructures: StructureReportItem[]; // Assuming top 5 are included
  structures: StructureReportItem[];
  startDate: string; // Need to pass these from the print page component
  endDate: string;   // Need to pass these from the print page component
  selectedType?: string; // Optional filter info
}

interface PrintableStructureReportProps {
  reportData: StructureReportData;
}

// Helper to format dates
const formatDate = (date: Date | string | null | undefined): string => {
  if (!date) return 'N/A';
  try {
    return format(new Date(date), 'dd.MM.yyyy');
  } catch (e) {
    return 'Invalid Date';
  }
};

// Helper to format currency
const formatCurrency = (value: number | null | undefined): string => {
  if (value === undefined || value === null) return '0 ₸';
  return new Intl.NumberFormat('ru-KZ', {
    style: 'currency',
    currency: 'KZT',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const PrintableStructureReport: React.FC<PrintableStructureReportProps> = ({ reportData }) => {
  if (!reportData) {
    return <div className="printable-container">Ошибка: Данные для отчета по конструкциям не найдены.</div>;
  }

  const {
    summary,
    structures,
    startDate,
    endDate,
    selectedType,
  } = reportData;

  return (
    <div className="printable-container structure-report-container">
      <header className="printable-header">
        <h1>Отчет по конструкциям</h1>
        <p>Период: {formatDate(startDate)} - {formatDate(endDate)}</p>
        {selectedType && <p>Тип конструкций: {selectedType}</p>}
      </header>

      <section className="summary-section">
        <h2>Сводка по периоду</h2>
        <div className="summary-grid">
          <div><strong>Всего конструкций:</strong> {summary.totalStructures}</div>
          <div><strong>Всего заказов:</strong> {summary.totalOrders}</div>
          <div><strong>Общий доход:</strong> {formatCurrency(summary.totalRevenue)}</div>
          <div><strong>Средняя загруженность:</strong> {summary.averageOccupancyRate.toFixed(1)}%</div>
        </div>
      </section>

      <section className="details-table">
        <h2>Детализация по конструкциям</h2>
        <table>
          <thead>
            <tr>
              <th>Код</th>
              <th>Тип</th>
              <th>Локация</th>
              <th>Загруженность (%)</th>
              <th>Доход (₸)</th>
              <th>Кол-во заказов</th>
              <th>Ср. длит. (дни)</th>
              <th>Кол-во клиентов</th>
            </tr>
          </thead>
          <tbody>
            {structures.map((structure) => (
              <tr key={structure.id}>
                <td>{structure.code}</td>
                <td>{structure.type}</td>
                <td>{structure.location}</td>
                <td>{structure.occupancyRate.toFixed(1)}%</td>
                <td>{formatCurrency(structure.revenue)}</td>
                <td>{structure.orderCount}</td>
                <td>{structure.avgDuration.toFixed(1)}</td>
                <td>{structure.clientCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* Optionally display top popular/profitable structures if needed */}
      {/* <section className="top-structures"> ... </section> */}

      <footer className="printable-footer">
        Отчет сформирован: {formatDate(new Date())}
      </footer>
    </div>
  );
};

export default PrintableStructureReport;
